<template><div><component-combinator component-name="DtChip" /><h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="non-interactive" tabindex="-1" class="d-docsite--header-3">Non Interactive <a class="header-anchor d-link d-docsite--link d-link" href="#non-interactive" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add <code v-pre>:interactive=&quot;false&quot;</code> to make it a read-only, non-interactive Chip. This changes it from a <code v-pre>&lt;button&gt;</code> to a non-interactive, read-only Chip with no events or hover/active state. Note that this does not effect the interactivity of its <code v-pre>×</code> remove button.</p>
<code-example source-code='&lt;dt-chip :interactive=&quot;false&quot;&gt;Chip&lt;/dt-chip&gt;'>
<dt-chip :interactive="false">Chip</dt-chip>
</code-example>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip&gt;Chip&lt;/dt-chip&gt;'>
<dt-chip>Chip</dt-chip>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>disabled</code> prop to disable both the Chip and its close button. This sets <code v-pre>aria-disabled=&quot;true&quot;</code> and <code v-pre>tabindex=&quot;-1&quot;</code> on the interactive elements and applies disabled styles, preventing pointer and keyboard interaction.</p>
<code-example source-code='&lt;dt-chip disabled&gt;Chip&lt;/dt-chip&gt;'>
<dt-chip disabled>Chip</dt-chip>
</code-example>
<h3 id="without-close-button" tabindex="-1" class="d-docsite--header-3">Without Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#without-close-button" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip :show-close=&quot;false&quot;&gt;Chip&lt;/dt-chip&gt;'>
<dt-chip :show-close="false">Chip</dt-chip>
</code-example>
<h3 id="with-icon" tabindex="-1" class="d-docsite--header-3">With Icon <a class="header-anchor d-link d-docsite--link d-link" href="#with-icon" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip :show-close=&quot;false&quot;&gt;
  &lt;template #icon&gt;
    &lt;dt-icon
      name=&quot;phone&quot;
      size=&quot;200&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #default&gt;
    Chip
  &lt;/template&gt;
&lt;/dt-chip&gt;'>
<dt-chip :show-close="false">
  <template #icon>
    <dt-icon
      name="phone"
      size="200"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
</code-example>
<h3 id="with-icon-and-close-button" tabindex="-1" class="d-docsite--header-3">With Icon and Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#with-icon-and-close-button" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip&gt;
  &lt;template #icon&gt;
    &lt;dt-icon
      name=&quot;phone&quot;
      size=&quot;200&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #default&gt;
    Chip
  &lt;/template&gt;
&lt;/dt-chip&gt;'>
<dt-chip>
  <template #icon>
    <dt-icon
      name="phone"
      size="200"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
</code-example>
<h3 id="with-avatar-and-close-button" tabindex="-1" class="d-docsite--header-3">With Avatar and Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#with-avatar-and-close-button" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip&gt;
  &lt;template #avatar&gt;
    &lt;dt-avatar
      image-src=&quot;/assets/images/person.png&quot;
      image-alt=&quot;Jaqueline Nackos&quot;
      full-name=&quot;Jaqueline Nackos&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #default&gt;
    Chip
  &lt;/template&gt;
&lt;/dt-chip&gt;'>
<dt-chip>
  <template #avatar>
    <dt-avatar
      image-src="/assets/images/person.png"
      image-alt="Jaqueline Nackos"
      full-name="Jaqueline Nackos"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
</code-example>
<h3 id="truncated" tabindex="-1" class="d-docsite--header-3">Truncated <a class="header-anchor d-link d-docsite--link d-link" href="#truncated" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">To truncate text, add <code v-pre>.d-truncate</code> to the content element, and set the width of the <code v-pre>.d-chip</code> element.</p>
<code-example source-code='&lt;dt-chip content-class=&quot;d-w-150&quot;&gt;
  &lt;span class=&quot;d-chip__text d-truncate&quot;&gt;Chip loooooong name&lt;/span&gt;
&lt;/dt-chip&gt;'>
<dt-chip content-class="d-w-150">
  <span class="d-chip__text d-truncate">Chip loooooong name</span>
</dt-chip>
</code-example>
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-chip :interactive=&quot;false&quot; :size=&quot;100&quot;&gt;Chip&lt;/dt-chip&gt;
&lt;dt-chip :interactive=&quot;false&quot; :size=&quot;200&quot;&gt;Chip&lt;/dt-chip&gt;
&lt;dt-chip :interactive=&quot;false&quot;&gt;Chip&lt;/dt-chip&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-chip :interactive="false" :size="100">Chip</dt-chip>
  <dt-chip :interactive="false" :size="200">Chip</dt-chip>
  <dt-chip :interactive="false">Chip</dt-chip>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="chip" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="chip" /></div></template>


